import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { applyActionCode } from "firebase/auth";
import { CheckCircle2, Loader2, MailCheck, TriangleAlert } from "lucide-react";
import { auth } from "@/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type ActionStatus = "loading" | "success" | "error";

function parseFirebaseActionError(error: unknown): string {
  const code =
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code?: unknown }).code === "string"
      ? (error as { code: string }).code
      : "";

  if (code.includes("invalid-action-code")) {
    return "رابط التحقق غير صالح. اطلب رسالة جديدة من التطبيق.";
  }
  if (code.includes("expired-action-code")) {
    return "رابط التحقق منتهي الصلاحية. اطلب رسالة جديدة من التطبيق.";
  }
  return "تعذر إتمام التحقق الآن. حاول مرة أخرى أو اطلب رسالة جديدة.";
}

export default function EmailAction() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<ActionStatus>("loading");
  const [message, setMessage] = useState("جاري تأكيد بريدك الإلكتروني...");

  const params = useMemo(() => {
    const fromRoute = new URLSearchParams(location.search);
    if (fromRoute.get("mode") && fromRoute.get("oobCode")) {
      return fromRoute;
    }

    // Fallback for malformed/old links where query appears before hash:
    // https://domain/?mode=...#/email-action
    const fromWindowSearch = new URLSearchParams(window.location.search);
    if (fromWindowSearch.get("mode") && fromWindowSearch.get("oobCode")) {
      return fromWindowSearch;
    }

    // Fallback for links with query embedded in hash path manually.
    const hash = window.location.hash || "";
    const queryFromHash = hash.includes("?") ? hash.slice(hash.indexOf("?")) : "";
    const fromHash = new URLSearchParams(queryFromHash);
    return fromHash;
  }, [location.search]);

  useEffect(() => {
    const mode = params.get("mode");
    const oobCode = params.get("oobCode");

    if (mode !== "verifyEmail") {
      setStatus("error");
      setMessage("هذا الرابط غير مدعوم حالياً داخل التطبيق.");
      return;
    }

    if (!oobCode) {
      setStatus("error");
      setMessage("رابط التحقق ناقص. اطلب رسالة تحقق جديدة.");
      return;
    }

    const run = async () => {
      try {
        await applyActionCode(auth, oobCode);
        if (auth.currentUser) {
          await auth.currentUser.reload();
        }
        setStatus("success");
        setMessage("تم تأكيد بريدك الإلكتروني بنجاح.");
      } catch (error) {
        setStatus("error");
        setMessage(parseFirebaseActionError(error));
      }
    };

    void run();
  }, [params]);

  const goNext = () => {
    if (auth.currentUser) {
      navigate("/", { replace: true });
      return;
    }
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center px-6 py-10"
      dir="rtl"
    >
      <Card className="w-full max-w-md border-2 overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />
        <CardContent className="pt-8 pb-6 space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary mx-auto flex items-center justify-center">
            {status === "loading" && <MailCheck className="h-7 w-7" />}
            {status === "success" && <CheckCircle2 className="h-7 w-7" />}
            {status === "error" && <TriangleAlert className="h-7 w-7" />}
          </div>

          <h1 className="text-center text-2xl font-bold text-foreground">
            تأكيد البريد الإلكتروني
          </h1>

          <p className="text-center text-sm text-muted-foreground">{message}</p>

          {status === "loading" ? (
            <Button className="w-full h-12 rounded-full" disabled>
              <Loader2 className="h-5 w-5 animate-spin ml-2" />
              جاري التحقق...
            </Button>
          ) : (
            <Button
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={goNext}
            >
              {status === "success" ? "الدخول إلى التطبيق" : "العودة لتسجيل الدخول"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
